import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  Search,
  Filter,
  MapPin,
  Star,
  Grid,
  List,
  SlidersHorizontal,
  Heart,
  Share2,
  Clock,
  Award,
  TrendingUp,
  Users,
  Bookmark,
  Bell,
  X,
  ChevronDown,
  Map as MapIcon
} from 'lucide-react';
import { SharedHeader } from './SharedHeader';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EnhancedSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [specialists, setSpecialists] = useState([]);
  const [filteredSpecialists, setFilteredSpecialists] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [savedSearches, setSavedSearches] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('random'); // 'random', 'rating', 'price_low', 'price_high'
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const SPECIALISTS_PER_PAGE = 12;
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || '');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [minRating, setMinRating] = useState(0);
  const [availabilityFilter, setAvailabilityFilter] = useState('anytime');
  const [distanceRadius, setDistanceRadius] = useState(20);
  
  // Results state
  const [totalResults, setTotalResults] = useState(0);
  const [showSaveSearch, setShowSaveSearch] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchSpecialists();
    loadSavedSearches();
  }, []);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [specialists, selectedCategory, searchQuery, priceRange, minRating, availabilityFilter, sortBy]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, priceRange, minRating, sortBy]);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (locationFilter) params.set('location', locationFilter);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedCategory, locationFilter, setSearchParams]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data || []);
    } catch (error) {
      console.error('Failed to load services:', error);
      // Fallback services
      setServices([
        { id: 'housekeeping', name: 'Housekeeping', icon: '🏠' },
        { id: 'handyman', name: 'Handyman', icon: '🔧' },
      ]);
    }
  };

  const fetchSpecialists = async () => {
    try {
      const response = await axios.post(`${API}/search/specialists/enhanced`, {
        service_category: '',
        location_filter: null,
        min_rating: 0,
        max_hourly_rate: 1000,
        sort_by: 'rating'
      });
      
      const specialistsData = response.data || [];
      setSpecialists(specialistsData);
      setTotalResults(specialistsData.length);
    } catch (error) {
      console.error('Failed to load specialists:', error);
      setSpecialists([]);
    }
    setLoading(false);
  };

  const loadSavedSearches = () => {
    // Mock saved searches - in real app, load from backend
    setSavedSearches([
      { id: 1, query: 'House Cleaning', location: 'Madrid', alerts: true },
      { id: 2, query: 'Handyman', location: 'Barcelona', alerts: false }
    ]);
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...specialists];

    // Category filter
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(s => 
        s.services && s.services.includes(selectedCategory)
      );
    }

    // Search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(query) ||
        s.bio?.toLowerCase().includes(query) ||
        s.services?.some(service => 
          getServiceName(service).toLowerCase().includes(query)
        )
      );
    }

    // Price range filter
    filtered = filtered.filter(s => 
      s.hourly_rate >= priceRange[0] && s.hourly_rate <= priceRange[1]
    );

    // Rating filter
    if (minRating > 0) {
      filtered = filtered.filter(s => (s.rating || 4.0) >= minRating);
    }

    // Sort results
    switch (sortBy) {
      case 'random':
        // Fisher-Yates shuffle for fair random distribution
        for (let i = filtered.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
        }
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'price_low':
        filtered.sort((a, b) => a.hourly_rate - b.hourly_rate);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.hourly_rate - a.hourly_rate);
        break;
      default:
        break;
    }

    setFilteredSpecialists(filtered);
    setTotalResults(filtered.length);
  };

  const getServiceIcon = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service && service.icon) return service.icon;
    
    const icons = {
      'housekeeping': '🏠',
      'handyman': '🔧',
    };
    return icons[serviceId] || '⚙️';
  };

  const getServiceName = (serviceId) => {
    const translationKey = serviceId?.replace('-', '') || 'others';
    return t(translationKey) !== translationKey ? t(translationKey) : serviceId;
  };

  const saveSearch = async () => {
    const searchData = {
      query: searchQuery || t('allServices'),
      location: locationFilter || t('anyLocation'),
      category: selectedCategory,
      priceRange,
      minRating,
      alerts: true
    };
    
    // In real app, save to backend
    const newSearch = {
      id: Date.now(),
      ...searchData
    };
    
    setSavedSearches(prev => [...prev, newSearch]);
    setShowSaveSearch(false);
    toast.success(t('searchSaved'));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setLocationFilter('');
    setPriceRange([0, 500]);
    setMinRating(0);
    setAvailabilityFilter('');
    setSortBy('rating');
  };

  const toggleFavorite = (specialistId) => {
    // Mock toggle favorite
    toast.success(t('addedToFavorites'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <SharedHeader showBackButton={true} backText={t('backToDashboard')} backPath="/" />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loadingSpecialists')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <SharedHeader showBackButton={true} backText={t('backToDashboard')} backPath="/" />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            {t('findYourPerfectSpecialist')}
          </h1>
          <p className="text-xl text-gray-700">{t('discoverTrustedProfessionals')}</p>
        </div>

        {/* Search Bar */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Main Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder={t('searchServices')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 text-lg"
                  />
                </div>
              </div>
              
              {/* Location Input */}
              <div className="lg:w-64">
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder={t('location')}
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
              </div>
              
              {/* Category Select */}
              <div className="lg:w-48">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder={t('allCategories')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allCategories')}</SelectItem>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        <div className="flex items-center gap-2">
                          <span>{getServiceIcon(service.id)}</span>
                          {getServiceName(service.id)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Filter Button */}
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className="h-12 px-6"
              >
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                {t('filters')}
              </Button>
              
              {/* Map View Button */}
              <Button 
                variant="outline" 
                onClick={() => navigate('/maps-demo')}
                className="h-12 px-6"
              >
                <MapIcon className="w-5 h-5 mr-2" />
                {t('mapView')}
              </Button>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Price Range */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">{t('priceRange')}</Label>
                    <div className="space-y-3">
                      <Slider
                        value={priceRange}
                        onValueChange={setPriceRange}
                        max={500}
                        step={5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>€{priceRange[0]}</span>
                        <span>€{priceRange[1]}+</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Minimum Rating */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">{t('minimumRating')}</Label>
                    <Select value={minRating.toString()} onValueChange={(value) => setMinRating(Number(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{t('anyRating')}</SelectItem>
                        <SelectItem value="3">3+ ⭐</SelectItem>
                        <SelectItem value="4">4+ ⭐</SelectItem>
                        <SelectItem value="4.5">4.5+ ⭐</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Availability */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">{t('availability')}</Label>
                    <Select value={availabilityFilter || "anytime"} onValueChange={setAvailabilityFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('anytime')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="anytime">{t('anytime')}</SelectItem>
                        <SelectItem value="today">{t('availableToday')}</SelectItem>
                        <SelectItem value="week">{t('thisWeek')}</SelectItem>
                        <SelectItem value="weekend">{t('weekends')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Distance */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">{t('distance')}</Label>
                    <Select value={distanceRadius.toString()} onValueChange={(value) => setDistanceRadius(Number(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 km</SelectItem>
                        <SelectItem value="10">10 km</SelectItem>
                        <SelectItem value="20">20 km</SelectItem>
                        <SelectItem value="50">{t('anywhere')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-between items-center mt-6">
                  <Button variant="ghost" onClick={clearFilters}>
                    {t('clearFilters')}
                  </Button>
                  
                  <div className="flex gap-2">
                    <Dialog open={showSaveSearch} onOpenChange={setShowSaveSearch}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Bookmark className="w-4 h-4 mr-2" />
                          {t('saveSearch')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('saveThisSearch')}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>{t('searchName')}</Label>
                            <Input placeholder={t('enterSearchName')} />
                          </div>
                          <div className="flex items-center gap-2">
                            <input type="checkbox" id="alerts" defaultChecked />
                            <Label htmlFor="alerts">{t('enableAlerts')}</Label>
                          </div>
                          <Button onClick={saveSearch} className="w-full">
                            <Bell className="w-4 h-4 mr-2" />
                            {t('saveWithAlerts')}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Tabs with Counts */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {/* All Tab */}
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                selectedCategory === 'all' 
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {t('allServices')} ({specialists.length})
            </button>
            
            {/* Housekeeping Tab */}
            <button
              onClick={() => setSelectedCategory('housekeeping')}
              className={`px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2 ${
                selectedCategory === 'housekeeping' 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>🏠</span>
              {t('housekeeping')} ({specialists.filter(s => s.services?.includes('housekeeping')).length})
            </button>
            
            {/* Handyman Tab */}
            <button
              onClick={() => setSelectedCategory('handyman')}
              className={`px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2 ${
                selectedCategory === 'handyman' 
                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>🔧</span>
              {t('handyman')} ({specialists.filter(s => s.services?.includes('handyman')).length})
            </button>
          </div>
          
          {/* Sort Options Row */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white rounded-lg p-4 shadow-sm">
            <div className="text-gray-700">
              <span className="font-semibold">{totalResults}</span> {totalResults === 1 ? t('specialistFound') : t('specialistsFound')}
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-600">{t('sortBy')}:</Label>
              <div className="flex gap-1">
                <button
                  onClick={() => setSortBy('random')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                    sortBy === 'random' 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t('random') || 'Случаен'}
                </button>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                    sortBy === 'rating' 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t('topRated')}
                </button>
                <button
                  onClick={() => setSortBy('price_low')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                    sortBy === 'price_low' 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t('priceLowToHigh') || 'Цена ↑'}
                </button>
                <button
                  onClick={() => setSortBy('price_high')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-all ${
                    sortBy === 'price_high' 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t('priceHighToLow') || 'Цена ↓'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Grid with Pagination */}
        {filteredSpecialists.length > 0 ? (
          <>
            <div className={viewMode === 'grid' ? 
              'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 
              'space-y-4'
            }>
              {filteredSpecialists
                .slice((currentPage - 1) * SPECIALISTS_PER_PAGE, currentPage * SPECIALISTS_PER_PAGE)
                .map((specialist, index) => (
                  <React.Fragment key={specialist.id}>
                    <SpecialistCard 
                      specialist={specialist}
                      viewMode={viewMode}
                      onFavorite={() => toggleFavorite(specialist.id)}
                      getServiceName={getServiceName}
                      getServiceIcon={getServiceIcon}
                    />
                    {/* Subtle divider after every 6 cards */}
                    {(index + 1) % 6 === 0 && index !== filteredSpecialists.slice((currentPage - 1) * SPECIALISTS_PER_PAGE, currentPage * SPECIALISTS_PER_PAGE).length - 1 && (
                      <div className="col-span-full my-4 flex items-center justify-center">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                        <span className="px-4 text-gray-400 text-sm">✦</span>
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
            </div>
            
            {/* Pagination */}
            {filteredSpecialists.length > SPECIALISTS_PER_PAGE && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ←
                </Button>
                
                {Array.from({ length: Math.ceil(filteredSpecialists.length / SPECIALISTS_PER_PAGE) }, (_, i) => i + 1).map(page => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className={currentPage === page ? 'bg-blue-600' : ''}
                  >
                    {page}
                  </Button>
                ))}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredSpecialists.length / SPECIALISTS_PER_PAGE), p + 1))}
                  disabled={currentPage === Math.ceil(filteredSpecialists.length / SPECIALISTS_PER_PAGE)}
                >
                  →
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('noSpecialistsFound')}</h3>
              <p className="text-gray-600 mb-6">{t('tryAdjustingFilters')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={clearFilters}>
                  {t('clearFilters')}
                </Button>
                <Button variant="outline" onClick={() => navigate('/auth-services')}>
                  {t('browseAllCategories')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Enhanced Specialist Card Component
const SpecialistCard = ({ specialist, viewMode, onFavorite, getServiceName, getServiceIcon }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
    onFavorite();
  };

  const handleCardClick = (e) => {
    e.stopPropagation();
    setShowPreview(true);
  };

  const handleViewFullProfile = () => {
    navigate(`/specialist/${specialist.id}`);
  };

  const shareProfile = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/specialist/${specialist.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${specialist.name} - ${t('specialist')}`,
          text: `Check out ${specialist.name} on SkillSOS!`,
          url
        });
      } catch (error) {
        navigator.clipboard.writeText(url);
        toast.success(t('linkCopied'));
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success(t('linkCopied'));
    }
  };

  // Preview Modal Component
  const PreviewModal = () => (
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start gap-4">
            <Avatar className="w-20 h-20 flex-shrink-0">
              <AvatarImage src={specialist.profile_image} />
              <AvatarFallback className="text-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                {specialist.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{specialist.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{(specialist.rating || 4.0).toFixed(1)}</span>
                <span className="text-gray-500 text-sm">({specialist.total_reviews || 0} {t('reviews')})</span>
              </div>
              <div className="flex items-center gap-1 text-gray-600 text-sm mt-1">
                <MapPin className="w-3 h-3" />
                <span>{specialist.location?.address || specialist.location?.city || 'България'}</span>
              </div>
            </div>
          </div>

          {/* Price & Availability */}
          <div className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
            <div>
              <div className="text-3xl font-bold text-blue-600">€{specialist.hourly_rate}</div>
              <div className="text-sm text-gray-600">{t('perHour') || 'на час'}</div>
            </div>
            <div className="text-right">
              <Badge className="bg-blue-100 text-blue-700">
                {t('minHours') || 'мин'}: {specialist.minimum_hours || 2}{t('hour') || 'ч'}
              </Badge>
              <div className="flex items-center gap-1 text-green-600 text-sm mt-2">
                <Clock className="w-3 h-3" />
                {t('availableToday')}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">{t('services') || 'Услуги'}</h4>
            <div className="flex flex-wrap gap-2">
              {specialist.services?.map((serviceId, index) => (
                <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                  <span className="mr-1">{getServiceIcon(serviceId)}</span>
                  {getServiceName(serviceId)}
                </Badge>
              ))}
            </div>
          </div>

          {/* Full Description */}
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">{t('aboutMe') || 'За мен'}</h4>
            <p className="text-gray-600 leading-relaxed">{specialist.bio || t('noBioYet') || 'Няма описание все още.'}</p>
          </div>

          {/* Experience */}
          {specialist.experience_years && (
            <div className="flex items-center gap-2 text-gray-600">
              <Badge variant="outline">{specialist.experience_years} {t('yearsExp')}</Badge>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleViewFullProfile}
            >
              {t('viewProfileAndBook') || 'Разгледай и резервирай'}
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={handleFavoriteClick}
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={shareProfile}
            >
              <Share2 className="w-5 h-5 text-gray-400" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (viewMode === 'list') {
    return (
      <>
        <PreviewModal />
        <Card className="hover:shadow-lg transition-all cursor-pointer" onClick={handleCardClick}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-14 h-14 flex-shrink-0">
                <AvatarImage src={specialist.profile_image} />
                <AvatarFallback className="text-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                  {specialist.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg text-gray-900 line-clamp-1">{specialist.name}</h3>
                  <div className="text-xl font-bold text-blue-600">€{specialist.hourly_rate}<span className="text-xs text-gray-500">/{t('hour') || 'ч'}</span></div>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium text-sm">{(specialist.rating || 4.0).toFixed(1)}</span>
                  </div>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600 text-sm line-clamp-1">{specialist.location?.city || 'България'}</span>
                </div>
                <p className="text-gray-500 text-sm mt-1 line-clamp-1">{specialist.bio}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  // Grid view - Simplified, cleaner cards
  return (
    <>
      <PreviewModal />
      <Card 
        className="hover:shadow-xl transition-all cursor-pointer transform hover:scale-[1.02] group overflow-hidden" 
        onClick={handleCardClick}
      >
        <CardContent className="p-5">
          {/* Top section - Avatar & Basic Info */}
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-14 h-14 flex-shrink-0 ring-2 ring-white shadow-md">
              <AvatarImage src={specialist.profile_image} />
              <AvatarFallback className="text-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                {specialist.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 line-clamp-1">{specialist.name}</h3>
              <div className="flex items-center gap-2 text-sm">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{(specialist.rating || 4.0).toFixed(1)}</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-500 line-clamp-1">{specialist.location?.city || 'България'}</span>
              </div>
            </div>
          </div>

          {/* Services badges */}
          <div className="flex flex-wrap gap-1 mb-3">
            {specialist.services?.slice(0, 2).map((serviceId, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {getServiceIcon(serviceId)} {getServiceName(serviceId)}
              </Badge>
            ))}
          </div>
          
          {/* Truncated description */}
          <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">
            {specialist.bio || t('noBioYet') || 'Кликнете за повече информация...'}
          </p>
          
          {/* Bottom section - Price & CTA */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div>
              <div className="text-xl font-bold text-blue-600">€{specialist.hourly_rate}</div>
              <div className="text-xs text-gray-500">{t('perHour') || 'на час'}</div>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4">
              {t('viewMore') || 'Виж повече'} →
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default EnhancedSearch;